import { useState, useEffect } from 'react'
import { CONTEUDO_CHAVES, getGaleria } from '@/api/adminApi'
import { useRequireConteudoFlag } from '@/utils/featureFlags'
import type { GaleriaItem } from '@/api/adminApi'
import { ClientPageLayout, clientPageStyles as layout } from '@/components/ClientPageLayout'

function inferTipoFromUrl(url: string): 'imagem' | 'video' {
  const value = url.toLowerCase()
  return /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(value) ? 'video' : 'imagem'
}

export function GaleriaPage() {
  const { loading: featureLoading } = useRequireConteudoFlag(CONTEUDO_CHAVES.menu_show_galeria)
  const [itens, setItens] = useState<GaleriaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<'todos' | 'imagem' | 'video'>('todos')

  useEffect(() => {
    let cancelled = false
    getGaleria(false)
      .then((list) => { if (!cancelled) setItens(list) })
      .catch(() => { if (!cancelled) setItens([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const itensComTipo = itens.map((i) => ({ ...i, tipo: i.tipo ?? inferTipoFromUrl(i.url) }))
  const imagens = itensComTipo.filter((i) => i.tipo === 'imagem')
  const videos = itensComTipo.filter((i) => i.tipo === 'video')
  const itensFiltrados =
    filtro === 'imagem' ? imagens : filtro === 'video' ? videos : itensComTipo

  const emptyFiltered = !loading && itens.length > 0 && itensFiltrados.length === 0

  if (featureLoading) return null

  return (
    <ClientPageLayout
      loading={loading}
      empty={!loading && itens.length === 0}
      emptyMessage="A galeria ainda não tem fotos ou vídeos."
      emptyIcon="🖼️"
    >
      {itensComTipo.length > 0 && (
        <div className={layout.chipRow}>
          <button
            type="button"
            className={`${layout.chip} ${filtro === 'todos' ? layout.chipActive : ''}`}
            onClick={() => setFiltro('todos')}
          >
            Todos ({itensComTipo.length})
          </button>
          <button
            type="button"
            className={`${layout.chip} ${filtro === 'imagem' ? layout.chipActive : ''}`}
            onClick={() => setFiltro('imagem')}
          >
            Fotos ({imagens.length})
          </button>
          <button
            type="button"
            className={`${layout.chip} ${filtro === 'video' ? layout.chipActive : ''}`}
            onClick={() => setFiltro('video')}
          >
            Vídeos ({videos.length})
          </button>
        </div>
      )}

      {emptyFiltered ? (
        <div className={layout.state}>
          <span className={layout.stateIcon} aria-hidden>
            🔍
          </span>
          <p>Nenhum item para este filtro.</p>
        </div>
      ) : (
        <div className={layout.galleryGrid}>
          {itensFiltrados.map((item) => (
            <figure key={item.id} className={layout.galleryItem}>
              {item.tipo === 'video' ? (
                <video src={item.url} className={layout.galleryMedia} controls preload="metadata" />
              ) : (
                <img src={item.url} alt={item.legenda ?? ''} className={layout.galleryMedia} />
              )}
              {item.legenda?.trim() && (
                <figcaption className={layout.galleryCaption}>{item.legenda}</figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </ClientPageLayout>
  )
}
